import { StandardEvents } from '@shopify/events';

/**
 * Attaches the current product, and the signed-in customer if there is one, to the
 * Shopify Forms forms on the page — so a quote request arrives identified by the
 * product the customer was looking at, pre-filled with their name/email.
 *
 * The Forms app renders each form inside the shadow root of a `shopify-forms-embed`
 * element, so its fields can't be reached with a document-wide selector and don't
 * surface in a document-wide MutationObserver. Each embed is therefore observed on
 * its own, and filled as soon as the app mounts a form into it.
 *
 * Fields are matched on the ids the app builds from the form's field keys, which
 * survive app releases — unlike its hashed class names.
 *
 * The product name/image fields also track the variant picker: a `shopify:product:select`
 * event on the page re-fills both fields, plus the visible thumbnail/title rendered
 * by `snippets/button.liquid`, with the newly selected variant's data. This element
 * lives inside the quote `<dialog>`, which sits outside the product-information
 * section in the DOM (dialogs aren't moved into it), so the section can't be relied
 * on to bubble the event here — the listener is document-wide instead, filtered by
 * matching `detail.productId` against this element's own product.
 */

const PRODUCT_NAME_FIELD = 'custom#the_product';
const PRODUCT_IMAGE_FIELD = 'custom#product_image';
const CUSTOMER_FIRST_NAME_FIELD = 'first_name';
const CUSTOMER_EMAIL_FIELD = 'email';

class FormsProductContext extends HTMLElement {
  /** Embeds already seen (for MutationObserver setup) and customer fields already filled once. */
  #handled = new WeakSet();

  /** @type {Set<MutationObserver>} */
  #observers = new Set();

  /** The product/variant title and image as rendered server-side, kept as the fallback for a variant with no image of its own. */
  #baseProductTitle = '';
  #baseProductImage = '';

  connectedCallback() {
    this.#baseProductTitle = this.dataset.productTitle ?? '';
    this.#baseProductImage = this.dataset.productImage ?? '';

    this.#scan();

    const embeds = new MutationObserver(() => this.#scan());
    embeds.observe(document.body, { childList: true, subtree: true });
    this.#observers.add(embeds);

    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    for (const observer of this.#observers) observer.disconnect();
    this.#observers.clear();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Refills the product fields/thumbnail with the newly selected variant once the
   * variant picker resolves, when the event is for this element's own product.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!this.dataset.productId) return;

    event.promise
      .then(({ detail }) => {
        if (!detail || String(detail.productId) !== this.dataset.productId) return;

        const resource = /** @type {Record<string, unknown> | null | undefined} */ (detail.resource);
        const rawTitle = resource && (resource.name || resource.title);
        const title = typeof rawTitle === 'string' && rawTitle ? rawTitle : this.#baseProductTitle;

        const featuredImage = /** @type {{ src?: string } | null | undefined} */ (resource?.featured_image);
        const image = featuredImage?.src ? this.#toAbsoluteUrl(featuredImage.src) : this.#baseProductImage;

        this.#updateVisibleProduct(title, image);

        for (const embed of document.querySelectorAll('shopify-forms-embed')) {
          if (embed.shadowRoot) this.#fillProductFields(embed.shadowRoot, title, image);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[forms-product-context] Event promise rejected:', error);
      });
  };

  /**
   * Updates the thumbnail/title shown above the form in `snippets/button.liquid`.
   * @param {string} title
   * @param {string} image
   */
  #updateVisibleProduct(title, image) {
    const dialog = this.closest('dialog');
    if (!dialog) return;

    const titleEl = dialog.querySelector('.button-form__product-title');
    if (titleEl) titleEl.textContent = title;

    const imageEl = dialog.querySelector('.button-form__product-image');
    if (imageEl instanceof HTMLImageElement && image) imageEl.src = image;
  }

  /**
   * @param {string} url
   * @returns {string}
   */
  #toAbsoluteUrl(url) {
    return url.startsWith('//') ? `https:${url}` : url;
  }

  /** Picks up form embeds as the app adds them to the page. */
  #scan() {
    for (const embed of document.querySelectorAll('shopify-forms-embed')) {
      const root = embed.shadowRoot;

      if (!root || this.#handled.has(embed)) continue;
      this.#handled.add(embed);

      this.#fill(root);

      const fields = new MutationObserver(() => this.#fill(root));
      fields.observe(root, { childList: true, subtree: true });
      this.#observers.add(fields);
    }
  }

  /**
   * @param {ShadowRoot} root - The shadow root the form is rendered into.
   */
  #fill(root) {
    this.#fillProductFields(root, this.#baseProductTitle, this.#baseProductImage);
    // Prefilled for convenience but left editable — a signed-in customer may be
    // requesting a quote for someone else, or want to use a different email. Filled
    // only once, unlike the product fields, so a variant change never clobbers
    // something the customer already typed.
    this.#fillOnce(root, CUSTOMER_FIRST_NAME_FIELD, this.dataset.customerFirstName);
    this.#fillOnce(root, CUSTOMER_EMAIL_FIELD, this.dataset.customerEmail);
  }

  /**
   * Sets the product name/image fields. Unlike the customer fields these are always
   * (re)written rather than filled once, so a variant change after the form has
   * mounted still reaches them — which product this is about isn't the customer's
   * to change, so there's nothing to clobber.
   * @param {ShadowRoot} root - The shadow root the form is rendered into.
   * @param {string} title
   * @param {string} image
   */
  #fillProductFields(root, title, image) {
    this.#setField(root, PRODUCT_NAME_FIELD, title, { readOnly: true });
    // A CDN link is metadata for whoever reads the submission, not something the
    // customer needs to look at, so this field is hidden once it holds the URL.
    this.#setField(root, PRODUCT_IMAGE_FIELD, image, { readOnly: true, hide: true });
  }

  /**
   * @param {ShadowRoot} root - The shadow root the form is rendered into.
   * @param {string} fieldId - Id of the field to fill.
   * @param {string} [value] - Value to fill it with. Nothing happens without one.
   * @param {object} [options]
   * @param {boolean} [options.hide] - Hide the field once it has been filled.
   * @param {boolean} [options.readOnly] - Prevent further edits to the field.
   */
  #setField(root, fieldId, value, { hide = false, readOnly = false } = {}) {
    if (!value) return;

    const field = this.#findField(root, fieldId);
    if (!field) return;

    field.value = value;
    // The form is a Preact app, and its text fields are controlled on `input`.
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.readOnly = readOnly;

    if (hide) field.parentElement?.style.setProperty('display', 'none');
  }

  /**
   * Fills a field only the first time it's seen, so a later re-render or variant
   * change can't overwrite something the customer already typed.
   * @param {ShadowRoot} root - The shadow root the form is rendered into.
   * @param {string} fieldId - Id of the field to fill.
   * @param {string} [value] - Value to fill it with. Nothing happens without one.
   */
  #fillOnce(root, fieldId, value) {
    if (!value) return;

    const field = this.#findField(root, fieldId);

    if (!field || this.#handled.has(field)) return;
    this.#handled.add(field);

    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /**
   * @param {ShadowRoot} root - The shadow root the form is rendered into.
   * @param {string} fieldId - Id of the field to look for.
   * @returns {HTMLInputElement | undefined}
   */
  #findField(root, fieldId) {
    // Shopify Forms appends a counter to a field key that collides with an existing
    // one, so `custom#product_image` can arrive as `custom#product_image_1`.
    return [...root.querySelectorAll('input')].find(
      (input) => input.id === fieldId || input.id.startsWith(`${fieldId}_`)
    );
  }
}

if (!customElements.get('forms-product-context')) {
  customElements.define('forms-product-context', FormsProductContext);
}
